import React from 'react';

export type SupportedSourceLanguage = 'English' | 'Spanish';

interface PlaySpeechStreamParams {
  canUseUpdatedSpeechstream?: boolean;
  idToRead?: string;
  playbackStateDispatch?: React.Dispatch<any>;
  voice?: string;
}

const IGNORED_SELECTORS = ['.vocab-block-bkg'];

const NORMAL_SPEED_VOICE = 'Matthew';
const SLOW_SPEED_VOICE = 'Joey';

// AIDEV-NOTE: "Reading speed" is implemented as a voice swap, not a playback rate — Joey reads
// slower than Matthew. Keep this the only place the speed → voice mapping lives.
export const voiceForSpeed = (speed?: string) => (
  speed === 'slow' ? SLOW_SPEED_VOICE : NORMAL_SPEED_VOICE
);

// Docs: https://docs.speechstream.net/home
const SpeechStream = {
  // AIDEV-NOTE: `speechstreamEventListenerAdded` only means "some hook instance registered the
  // one-time global setup" — it does NOT mean the library is usable. Every instance registers its own
  // `markLoaded` listener (and falls back to `speechstreamToolbarLoaded` when it mounts after the
  // event) so a late-mounting button can't report loaded before window.speechstream exists, which
  // would silently swallow its setVoice/setNoScroll calls.
  addEventListener: ({ locale, page, setIsLoaded, showSpeechstreamToolbar, voiceRef }) => {
    const markLoaded = () => setIsLoaded(true);
    const teardown = () => window.removeEventListener('speechToolbarLoaded', markLoaded);

    if (window.speechstreamToolbarLoaded) setIsLoaded(true);
    window.addEventListener('speechToolbarLoaded', markLoaded);

    if (window.speechstreamEventListenerAdded) return teardown;

    window.addEventListener('speechToolbarLoaded', () => {
      window.speechstreamToolbarLoaded = true;
      const voice = voiceRef?.current;

      if (showSpeechstreamToolbar) {
        $('#th_toolbar').css('display', 'initial');
      }

      if (locale && locale.toUpperCase() === 'ES') {
        SpeechStream.setSpeechBookID('ESALL');
        SpeechStream.setVoice('Paulina');
      } else {
        SpeechStream.setSpeechBookID('ALL');

        if (voice) {
          SpeechStream.setVoice(voice);
        }
      }
      if (page) SpeechStream.setSpeechPageID(page);

      window.speechstream.speechTools.setSpeechStartedCallback(() => {
        if (!window.lastIdSpoken || !window.speechstreamPlayButtons) return;

        Object.keys(window.speechstreamPlayButtons)
          .filter(buttonId => buttonId !== (window.lastIdSpoken || '0'))
          .forEach((id) => {
            window.speechstreamPlayButtons[id].stopPlaying();
          });
      });

      window.speechstream.speechTools.setSpeechStoppedCallback(() => {
        if (!window.speechstreamPlayButtons) return;
        if (!window.lastIdSpoken) {
          return window.speechstreamPlayButtons[0] && window.speechstreamPlayButtons[0].stopPlaying();
        }

        return window.speechstreamPlayButtons[window.lastIdSpoken]
          && window.speechstreamPlayButtons[window.lastIdSpoken].stopPlaying();
      });

      SpeechStream.ignoreSelectors(IGNORED_SELECTORS);
      SpeechStream.hideDecorativeSprites();
    });

    window.speechstreamEventListenerAdded = true;
    window.speechstreamPlayButtons = {};

    return teardown;
  },
  // AIDEV-NOTE: The SpeechStream/Browsealoud toolbar injects SVG sprite sheets (<defs><symbol>) into
  // an open shadow root under #__bs_entryDiv. They render nothing on their own but are exposed to AT
  // as unnamed images, failing WCAG 1.1.1. Mark them aria-hidden. Retry briefly since the vendor may
  // inject them a tick after the load event. Only sprite sheets (svgs with a <symbol>) are hidden, so
  // a meaningful standalone icon could never be suppressed by accident.
  hideDecorativeSprites: () => {
    const spriteSheets = () => {
      const entry = document.getElementById('__bs_entryDiv');
      if (!entry) return [];

      const host = Array.from(entry.querySelectorAll('div')).find(div => div.shadowRoot);
      if (!host?.shadowRoot) return [];

      return Array.from(host.shadowRoot.querySelectorAll('svg')).filter(svg => svg.querySelector('symbol'));
    };

    let attempts = 0;
    const mark = () => {
      const svgs = spriteSheets();

      if (svgs.length) {
        svgs.forEach(svg => svg.setAttribute('aria-hidden', 'true'));
      } else if (attempts++ < 20) {
        setTimeout(mark, 250);
      }
    };

    mark();
  },
  ignoreSelectors: (selectors) => {
    selectors.forEach((selector) => {
      window.speechstream.domControlTools.getNewDomControl(window.document).addIgnoreListQuerySelector(selector);
    });
  },
  pause: (playbackStateDispatch) => {
    playbackStateDispatch({ type: 'TOGGLE' });
    window.speechstream.speechTools.pause();
  },
  // AIDEV-NOTE: The voice is re-applied on every play. Setting it once when the toolbar loads isn't
  // enough — the vendor can finish initializing after that event and fall back to its default voice,
  // which is what made a saved "slow" speed read at standard speed until you re-picked it (PLAT-17863).
  play: ({ idToRead = 'text_to_speech_start', playbackStateDispatch, voice }: PlaySpeechStreamParams) => {
    if (voice) SpeechStream.setVoice(voice);

    playbackStateDispatch({ type: 'PLAY' });
    const selection = window.getSelection().toString();
    const node = selection ? $(`#${idToRead} :contains("${selection}")`).last() : document.getElementById(idToRead);

    window.speechstreamPlayButtons[idToRead] = { stopPlaying: () => playbackStateDispatch({ type: 'STOP' }) };
    window.lastIdSpoken = idToRead;

    if (selection) {
      SpeechStream.setNoScroll(false);
      SpeechStream.setStartPoint(node);
      SpeechStream.setStopAfter(idToRead);
      SpeechStream.setStopAfter(idToRead);
      window.speechstream.speechTools.play();
    } else {
      window.speechstream.speechTools.speakElement(node, false);
    }
  },
  selectedDestinationLanguage: () => {
    const response = window.speechstream?.textTools?.getTranslateDestination();

    return (response || {}).englishDescription;
  },
  setNoScroll: (shouldNotScroll: boolean) => window.speechstream.domControlTools.setNoScroll(shouldNotScroll),
  setSpeechBookID: bookId => window.speechstream.speechTools.setSpeechBookID(bookId),
  setSpeechPageID: pageId => window.speechstream.speechTools.setSpeechPageID(pageId),
  setStartPoint: node => window.speechstream.domControlTools.setStartPoint(node),
  setStopAfter: (id) => {
    window.speechstream.domControlTools.getNewDomControl(window.document).addCommand(id, 'stopAfter');
  },
  setTranslationDestination: (language: string): Promise<{ englishDescription: string }> => (
    window.speechstream?.textTools?.setTranslateDestination(language)
  ),
  setTranslationSource: (language: SupportedSourceLanguage): SetSpeechstreamSourceLanguage => (
    window.speechstream?.textTools?.setTranslateSource(language)
  ),
  setVoice: (voiceName) => {
    if (window.speechstream && window.speechstream.speechTools) window.speechstream.speechTools.setVoice(voiceName);
  },
  stop: (playbackStateDispatch = null) => {
    if (playbackStateDispatch) playbackStateDispatch({ type: 'STOP' });
    window.speechstream.speechTools.stop();
  },
  translate: () => {
    window.speechstream.textTools.translate();
  },
  translationLanguages: () => {
    const list = window.speechstream?.textTools?.getLanguageList() || {};
    return ([...list['Lanaguages with a voice'], ...list['Lanaguages without a voice']] || []).sort();
  },
  translationLanguagesMap: () => {
    const list = window.speechstream?.textTools?.getLanguageList() || {};
    const withVoice = list['Lanaguages with a voice'] || [];
    const withoutVoice = list['Lanaguages without a voice'] || [];

    return {
      ...Object.fromEntries(withVoice.map((lang: string) => [lang, true])),
      ...Object.fromEntries(withoutVoice.map((lang: string) => [lang, false])),
    };
  },
  eventListenerAdded: () => window.speechstreamEventListenerAdded,
};

export default SpeechStream;
